<template>
  <b-card
    no-body
    class="drag mb-1"
    v-bind:class="{ deleteMe: variable.delete_me }"
  >
    <b-card-header
      header-tag="header"
      class="p-1 d-flex align-items-center justify-content-between"
      role="tab"
    >
      <h5
        class="text-left my-1 d-flex align-items-center"
        v-b-toggle="'var_' + (variable.id | variable.uid)"
      >
        <open-icon
          :name="variable.visible ? 'chevron-bottom' : 'chevron-right'"
        />
        <i
          class="mdi mdi-format-align-justify drag-element d-flex"
          aria-hidden="true"
        />
        <b-badge variant="info" class="m-1 test">
          {{ variable.delete_me ? 'x' : variable.order }}
        </b-badge>
        <b-form-input
          type="text"
          class="variable-label-input col-8"
          v-model="variable.input_label"
        >
        </b-form-input>
      </h5>
      <div class="actions my-1">
        <b-btn
          size="sm"
          variant="default"
          @click="$emit('delete-variable', variable)"
        >
          <open-icon name="delete" />
        </b-btn>
      </div>
    </b-card-header>

    <b-collapse
      :id="'var_' + (variable.id | variable.uid)"
      role="tabpanel"
      v-model="variable.visible"
    >
      <b-card-body>
        <template v-if="question.type === 'MULTI_VARIABLE_SELECT_ONE'">
          <b-row>
            <b-col class="d-flex align-item-center justify-content-center mb-3">
              <b-form-group
                :invalid-feedback="invalidOptionsFeedback"
                :state="validateOptionsState(variable)"
                class="text-center"
              >
                <b-button
                  @click="onCreateOption(variable.id || variable.uid)"
                  variant="primary"
                  size="sm"
                >
                  Add Option
                </b-button>
              </b-form-group>
            </b-col>
          </b-row>
          <template v-for="(option, index) in variable.variableOptions">
            <variable-option
              :key="(option.id || option.uid)"
              :index="parseInt(index) + 1"
              :option="option"
              @removeOption="onRemoveOption"
              @cancelRemoveOption="onCancelRemoveOption"
            />
          </template>
        </template>
        <template v-else>
          <!-- variable fields -->
          <b-form-group label="Short Label" label-cols="4">
            <b-form-input type="text" v-model="variable.short_label" />
          </b-form-group>

          <b-form-group
            v-if="question.type === 'SELECT_ANY'"
            label="Exclusive"
            label-cols="4"
            label-class="py-0"
          >
            <b-form-checkbox
              :checked="exclusiveId === variable.id"
              @change="(checked) => updateExclusive(checked, variable.id)"
            />
          </b-form-group>

          <div v-if="question.type === 'SLIDER'">
            <b-form-group label="Prefix" label-cols="4">
              <b-form-input type="text" v-model="variable.prefix" />
            </b-form-group>
            <b-form-group label="Suffix" label-cols="4">
              <b-form-input type="text" v-model="variable.suffix" />
            </b-form-group>
            <b-form-group label="Singular Suffix" label-cols="4">
              <b-form-input type="text" v-model="variable.singular_suffix" />
            </b-form-group>
            <b-form-group label="Minimum Value" label-cols="4">
              <b-form-input type="text" v-model="variable.min" />
            </b-form-group>
            <b-form-group label="Minimum Label" label-cols="4">
              <b-form-input v-model="variable.min_label" />
            </b-form-group>
            <b-form-group label="Maximum Value" label-cols="4">
              <b-form-input type="text" v-model="variable.max" />
            </b-form-group>
            <b-form-group label="Maximum Label" label-cols="4">
              <b-form-input v-model="variable.max_label" />
            </b-form-group>
            <b-form-group label="Slider Increment" label-cols="4">
              <b-form-input type="text" v-model="variable.step" min="1" />
            </b-form-group>
            <b-form-group label="Set Zero Label" label-cols="4">
              <b-form-input v-model="variable.set_zero_label" />
            </b-form-group>
          </div>

          <b-form-group
            v-show="question.type === 'CONFIRMATION'"
            label="Derived Variable"
            label-cols="4"
          >
            <b-form-select
              v-model="variable.dv_name"
              :options="derivedVarOptions"
            >
            </b-form-select>
          </b-form-group>
        </template>
        <b-row v-if="question.type !== 'YES_NO'">
          <b-col cols="12" class="text-center">
            Show this variable only if this variable is true:
          </b-col>
          <b-col cols="12" class="text-center mt-2">
            <b-button
              variant="primary"
              @click="conditionSelectorVisible = true"
            >
              Select variable
            </b-button>
          </b-col>
          <b-col
            cols="12"
            v-if="conditionSelectorVisible || variable.condition"
          >
            <variable-condition-selector
              :condition="visibilityValidationCondition"
              @set-visibility-condition="
                $emit('set-visibility-condition', $event)
              "
            />
          </b-col>
        </b-row>
        <b-row
          v-if="question.type === 'SLIDER' && !question.calculateTotals"
          class="mt-2"
        >
          <b-col cols="12">
            <value-condition-form
              :condition="responseValidationCondition"
              @set-condition="
                $emit('set-response-validation-condition', $event)
              "
            >
              <span slot="label-text">
                Select a question variable and operator to validate this
                variable response against:
              </span>
            </value-condition-form>
          </b-col>
        </b-row>
      </b-card-body>
    </b-collapse>
  </b-card>
</template>

<script>
import { createNewVariableOption } from '@/helpers/VariableOptionHelper';
import VariableOption from '@/components/question/variable/VariableOption';
import DerivedVariables from '@/providers/DerivedVariables';
import VariableConditionSelector from '@/components/question/variable/VariableConditionSelector';
import ValueConditionForm from '@/components/question/condition/ValueConditionForm';
import { CONDITION_VARIANTS } from '@/store';
import cloneDeep from 'lodash/cloneDeep';

export default {
  name: 'VariableEditForm',
  props: {
    initialVariable: Object,
    question: Object,
    exclusiveId: {
      default: null,
      validator: (prop) => typeof prop === 'number' || prop === null,
    },
  },
  components: {
    VariableOption,
    VariableConditionSelector,
    ValueConditionForm,
  },
  data: function () {
    return {
      conditionSelectorVisible: false,
      variable: cloneDeep(this.initialVariable),
    };
  },
  computed: {
    derivedVarOptions: function () {
      return Object.keys(DerivedVariables.calculationVariables);
    },
    invalidOptionsFeedback: function () {
      return 'Variable should contain at least one option';
    },
    visibilityValidationCondition: function () {
      return this.variable && Array.isArray(this.variable.condition)
        ? this.variable.condition.find(
            (cond) => cond.variant === CONDITION_VARIANTS.VISIBILITY
          )
        : null;
    },
    responseValidationCondition: function () {
      return this.variable && Array.isArray(this.variable.condition)
        ? this.variable.condition.find(
            (cond) => cond.variant === CONDITION_VARIANTS.RESPONSE_VALIDATION
          )
        : null;
    },
  },
  methods: {
    updateExclusive: function (checked, vId) {
      this.$emit('update-exclusive', checked ? vId : null);
    },
    onCreateOption: function (varId) {
      const option = createNewVariableOption(varId);
      this.$emit('add-variable-option', varId, option);
    },
    onRemoveOption: function (optionId, variableId) {
      this.$emit('remove-option', optionId, variableId);
    },
    onCancelRemoveOption: function (optionId, variableId) {
      this.$emit('cancel-remove-option', optionId, variableId);
    },
    validateOptionsState: function (variable) {
      const { $dirty, minLength, required } = variable.variableOptions;
      return $dirty ? minLength && required : null;
    },
    onSetResponseValidationCondition: function (newCondition) {
      const condition = {
        ...newCondition,
        variant: CONDITION_VARIANTS.RESPONSE_VALIDATION,
      };
      if (!Array.isArray(this.variable.condition)) {
        this.variable.condition = [condition];
      } else {
        const index = this.variable.condition.findIndex(
          (cond) => cond.variant === CONDITION_VARIANTS.RESPONSE_VALIDATION
        );
        if (index === -1) {
          this.variable.condition.push(condition);
        } else {
          this.$set(this.variable.condition, index, condition);
        }
      }
    },
  },
  watch: {
    variable: function (newVar) {
      this.$emit('update-variable', newVar);
    },
  },
};
</script>

<style scoped lang="scss">
.drag-element {
  cursor: move;
}
</style>
